
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FormSection from '../../components/formSection/FormSection.jsx';
import CustomSelect from '../../components/formSection/CustomSelect.jsx';
import { usePhoneMask, validateEmailInput, isValidEmail, formatPhoneNumber } from '../../lib/phone.js';
import {
  capitalizeFirstLetter,
  capitalizeEachWord,
  downloadBrowserFile,
  formatContactPersonName,
  formatFoundingYear,
  normalizeWebsiteUrl,
} from '../../lib/formFormatters.js';
import { apiUrl } from '../../lib/apiBase.js';
import { apiFetch, escapeHtml, showToast } from '../../lib/utils.js';
import { Icon, loadIcons } from '@iconify/react';
import {
  Activity,
  ArrowLeft,
  Building2,
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  Edit2,
  Layers,
  Loader2,
  Package,
  Percent,
  PlusCircle,
  ShoppingBag,
  Star,
  Tag,
  ThumbsUp,
  Trash2,
  TrendingUp,
  Trophy,
  UploadCloud,
  X,
  Zap,
  File,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import photo_logo from '../../assets/logo-big_black.svg';
import "./form.css";

const TOTAL_STEPS = 4;
const CURRENT_YEAR = new Date().getFullYear();
const STEP_NAMES = ['Про компанію', 'Комерційні умови', 'Інформація про товари', 'Фінальний рейтинг'];
const MAX_FILE_BYTES = 16 * 1024 * 1024;
const ALLOWED_FILE_EXTS = new Set(['pdf', 'xlsx', 'xls']);

function isAllowedPricingFile(filename) {
  if (!filename || !filename.includes('.')) return false;
  const ext = filename.split('.').pop().toLowerCase();
  return ALLOWED_FILE_EXTS.has(ext);
}

function getFileIcon(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  const icons = {
    pdf: 'file-text',
    xlsx: 'file-spreadsheet',
    xls: 'file-spreadsheet',
  };
  return icons[ext] || 'file';
}

function FileGlyph({ name }) {
  const k = getFileIcon(name);
  if (k === 'file-spreadsheet') return <FileSpreadsheet className="w-5 h-5 text-white" />;
  if (k === 'file') return <File className="w-5 h-5 text-white" />;
  return <FileText className="w-5 h-5 text-white" />;
}

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'Виробник', label: 'Виробник', icon: 'mdi:factory' },
  { value: "Дистриб'ютор", label: "Дистриб'ютор", icon: 'mdi:truck-delivery' },
  { value: 'Підприємець', label: 'Підприємець', icon: 'mdi:account-tie' },
];

const FORM_PRELOAD_ICONS = [
  'solar:buildings-bold',
  'heroicons:user-solid',
  'basil:document-solid',
  'fluent:box-32-filled',
  'fluent:payment-16-filled',
  'mage:delivery-truck-fill',
  'mingcute:certificate-fill',
  'mingcute:upload-3-fill',
  'solar:document-bold',
  'bxs:category-alt',
  'iconamoon:category-fill',
  'lucide:chevron-down',
  ...ACTIVITY_TYPE_OPTIONS.map((o) => o.icon),
];

const defaultFields = {
  company_name: '',
  contact_person: '',
  contact_position: '',
  contact_phone: '',
  contact_email: '',
  website: '',
  legal_address: '',
  founding_year: '',
  bank_details: '',
  specialization: '',
  activity_types: [],
  additional_info: '',
  fax_number: '',
  moq_units: '',
  moq_amount: '',
  payment_terms: '',
  deferment_days: '90',
  return_details: '',
};

const defaultRadios = {
  electronic_docs: '',
  returns: '',
  delivery: '',
  certificates: '',
};

function emptyProductDraft() {
  return {
    product_group: '',
    product_name: '',
    product_brand: '',
    product_vat: '20',
    product_markup_manual: '',
    product_turnover: '',
    product_avg_sales: '',
  };
}

/** Multipart: use apiUrl (not CRA origin) and never force Content-Type. */
async function postMultipart(url, formData) {
  const target = apiUrl(url);
  const isCrossOrigin =
    /^https?:\/\//i.test(target) &&
    typeof window !== 'undefined' &&
    !target.startsWith(window.location.origin);

  return fetch(target, {
    method: 'POST',
    body: formData,
    credentials: isCrossOrigin ? 'include' : 'same-origin',
  });
}

async function readApiError(response, fallback) {
  try {
    const body = await response.json();
    if (body?.error) return String(body.error);
  } catch {
    /* ignore */
  }
  return fallback;
}

export default function FormPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [fields, setFields] = useState(() => ({ ...defaultFields }));
  const [radios, setRadios] = useState(() => ({ ...defaultRadios }));
  const [addedProducts, setAddedProducts] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [pricingFiles, setPricingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [productModalClosing, setProductModalClosing] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productDraft, setProductDraft] = useState(emptyProductDraft);
  const [productOpsOpen, setProductOpsOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [rating, setRating] = useState({
    score: 0,
    target: 0,
    text: '',
    strengths: [],
    weaknesses: [],
    stars: [false, false, false, false, false],
    ready: false,
  });
  const ratingAnimRef = useRef(null);
  const formContentRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [submitFinished, setSubmitFinished] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [iconsReady, setIconsReady] = useState(false);

  const phoneMask = usePhoneMask('');
  const fileInputRef = useRef(null);
  const step4RunRef = useRef(0);
  const submitInFlightRef = useRef(false);
  const emailErrorRef = useRef(null);

  const setField = useCallback((id, value) => {
    setFields((prev) => ({ ...prev, [id]: value }));
    setFieldErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setRadio = useCallback((name, value) => {
    setRadios((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await apiFetch('/api/product-groups');
        if (response.ok) {
          const groups = await response.json();
          setProductGroups(Array.isArray(groups) ? groups : []);
        }
      } catch (e) {
        console.error('Error loading product groups:', e);
      }
    })();
  }, []);

  useEffect(() => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      setIconsReady(true);
    };

    loadIcons(FORM_PRELOAD_ICONS, () => finish());
    const fallback = window.setTimeout(finish, 1800);
    return () => {
      settled = true;
      window.clearTimeout(fallback);
    };
  }, []);

  const calculateStepProgress = useCallback(
    (step) => {
      const mandatoryFieldsMap = {
        1: [
          'company_name',
          'legal_address',
          'founding_year',
          'bank_details',
          'specialization',
          'contact_person',
          'contact_position',
          'contact_phone',
          'contact_email',
        ],
        2: ['payment_terms'],
        3: [],
      };
      let totalFields = 0;
      let filledFields = 0;
      const mandatoryFields = mandatoryFieldsMap[step] || [];
      mandatoryFields.forEach((id) => {
        const value = (fields[id] || '').trim();
        totalFields += 1;
        if (value !== '') filledFields += 1;
      });
      if (step === 1) {
        totalFields += 2;
        if (radios.electronic_docs) filledFields += 1;
        if (Array.isArray(fields.activity_types) && fields.activity_types.length > 0) {
          filledFields += 1;
        }
      }
      if (step === 2) {
        totalFields += 3;
        if (radios.returns) filledFields += 1;
        if (radios.delivery) filledFields += 1;
        if (radios.certificates) filledFields += 1;
        if (fields.payment_terms === 'deferment') {
          totalFields += 1;
          if ((fields.deferment_days || '').trim() !== '') filledFields += 1;
        }
        if (radios.returns === 'yes') {
          totalFields += 1;
          if ((fields.return_details || '').trim() !== '') filledFields += 1;
        }
      }
      if (step === 3) {
        totalFields = 2;
        if (addedProducts.length > 0) filledFields += 1;
        if (pricingFiles.length > 0) filledFields += 1;
      }
      return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
    },
    [fields, radios, addedProducts.length, pricingFiles.length],
  );

  const totalProgress = useMemo(() => {
    const a = calculateStepProgress(1);
    const b = calculateStepProgress(2);
    const c = calculateStepProgress(3);
    return Math.round((a + b + c) / 3);
  }, [calculateStepProgress]);

  const validateStep1 = useCallback(
    (showErrors = false) => {
      const mandatoryFields = [
        'company_name',
        'legal_address',
        'founding_year',
        'bank_details',
        'specialization',
        'contact_person',
        'contact_position',
        'contact_phone',
        'contact_email',
      ];
      const errors = {};
      for (const id of mandatoryFields) {
        const v = (fields[id] || '').trim();
        if (!v) errors[id] = true;
      }
      if (!radios.electronic_docs) errors.electronic_docs = true;
      if (!Array.isArray(fields.activity_types) || fields.activity_types.length === 0) {
        errors.activity_types = true;
      }
      const email = (fields.contact_email || '').trim();
      if (email && !isValidEmail(email)) errors.contact_email = true;

      if (showErrors) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          const keys = [...mandatoryFields, 'electronic_docs', 'contact_email', 'activity_types'];
          for (const id of keys) {
            if (errors[id]) next[id] = true;
            else delete next[id];
          }
          return next;
        });
      }

      return Object.keys(errors).length === 0;
    },
    [fields, radios.electronic_docs],
  );

  const validateStep2 = useCallback(
    (showErrors = false) => {
      const errors = {};

      const paymentTerms = fields.payment_terms || '';
      if (!paymentTerms) errors.payment_terms = true;
      if (paymentTerms === 'deferment') {
        if (!(fields.deferment_days || '').trim()) errors.deferment_days = true;
      }

      if (!radios.returns) errors.returns = true;
      if (radios.returns === 'yes') {
        if (!(fields.return_details || '').trim()) errors.return_details = true;
      }
      if (!radios.delivery) errors.delivery = true;
      if (!radios.certificates) errors.certificates = true;

      if (showErrors) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          const keys = [
            'moq_units',
            'moq_amount',
            'payment_terms',
            'deferment_days',
            'returns',
            'return_details',
            'delivery',
            'certificates',
          ];
          for (const id of keys) {
            if (errors[id]) next[id] = true;
            else delete next[id];
          }
          return next;
        });
      }

      return Object.keys(errors).length === 0;
    },
    [
      fields.deferment_days,
      fields.payment_terms,
      fields.return_details,
      radios.certificates,
      radios.delivery,
      radios.returns,
    ],
  );

  const validateStep3 = useCallback(
    (showErrors = false) => {
      const errors = {};
      if (pricingFiles.length === 0) errors.pricing_files = true;
      if (addedProducts.length === 0) errors.products = true;

      if (showErrors) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          if (errors.pricing_files) next.pricing_files = true;
          else delete next.pricing_files;
          if (errors.products) next.products = true;
          else delete next.products;
          return next;
        });
      }

      return Object.keys(errors).length === 0;
    },
    [addedProducts.length, pricingFiles.length],
  );

  const canAccessStep = useCallback(
    (step, showErrors = false) => {
      if (step <= 1) return true;
      if (step === 2) return validateStep1(showErrors);
      if (step === 3) return validateStep1(showErrors) && validateStep2(showErrors);
      if (step === 4) return validateStep1(showErrors) && validateStep2(showErrors) && validateStep3(showErrors);
      return false;
    },
    [validateStep1, validateStep2, validateStep3],
  );

  const resetFormToStart = useCallback(() => {
    setSuccessModalOpen(false);
    setSubmitFinished(false);
    setApplicationId(null);
    setIsSubmitting(false);
    setRatingLoading(false);
    setFields({ ...defaultFields });
    setRadios({ ...defaultRadios });
    setAddedProducts([]);
    setPricingFiles([]);
    setFieldErrors({});
    setProductDraft(emptyProductDraft());
    setProductFormOpen(false);
    setProductModalVisible(false);
    setProductModalClosing(false);
    setEditingProductId(null);
    setProductOpsOpen(false);
    setRating({
      score: 0,
      target: 0,
      text: '',
      strengths: [],
      weaknesses: [],
      stars: [false, false, false, false, false],
      ready: false,
    });
    setCurrentStep(1);
    if (emailErrorRef.current) emailErrorRef.current.classList.add('hidden');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem('supplier_form_state_v1');
    } catch {
      /* ignore */
    }
  }, []);

  const notifyMissingFields = useCallback(
    (
      title = 'Отакої, ви щось забули заповнити!',
      message = 'Будь-ласка, перевірте заповнені вами поля, бо ви щось пропустили',
    ) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      showToast(message, 'validation', { title });
    },
    [],
  );

  const skipStepEnterRef = useRef(true);

  const playStepEnter = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const el = formContentRef.current;
    if (!el) return;
    el.classList.remove('is-step-enter');
    // reflow, щоб CSS-анімація перезапустилась
    void el.offsetWidth;
    el.classList.add('is-step-enter');
  }, []);

  // Анімація після зміни кроку (Продовжити / Повернутися / степер у хедері)
  useEffect(() => {
    if (skipStepEnterRef.current) {
      skipStepEnterRef.current = false;
      return;
    }
    playStepEnter();
  }, [currentStep, playStepEnter]);

  const goToStep = useCallback(
    (step) => {
      if (!canAccessStep(step, true)) {
        notifyMissingFields(
          'Отакої, ви щось забули заповнити!',
          'Перейдіть на вкладку можна лише після заповнення попередніх кроків.',
        );
        return;
      }
      if (step === currentStep) return;
      setCurrentStep(step);
    },
    [canAccessStep, currentStep, notifyMissingFields],
  );

  const nextStep = useCallback(() => {
    if (currentStep < TOTAL_STEPS) {
      if (currentStep === 1) {
        if (!validateStep1(true)) {
          notifyMissingFields();
          return;
        }
      } else if (currentStep === 2) {
        if (!validateStep2(true)) {
          notifyMissingFields();
          return;
        }
      } else if (currentStep === 3) {
        if (!validateStep3(true)) {
          notifyMissingFields();
          return;
        }
      }
      setCurrentStep((s) => s + 1);
    }
  }, [currentStep, notifyMissingFields, validateStep1, validateStep2, validateStep3]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const mergeFiles = useCallback((incoming) => {
    if (!incoming || !incoming.length) return;
    const allowed = [];
    const rejected = [];
    for (const file of incoming) {
      if (isAllowedPricingFile(file.name)) allowed.push(file);
      else rejected.push(file.name);
    }
    if (rejected.length) {
      showToast(
        `Дозволені лише PDF та Excel. Відхилено: ${rejected.join(', ')}`,
        'error',
      );
    }
    if (!allowed.length) return;
    setPricingFiles((prev) => [...prev, ...allowed]);
    setFieldErrors((prev) => {
      if (!prev.pricing_files) return prev;
      const next = { ...prev };
      delete next.pricing_files;
      return next;
    });
  }, []);

  const removeFileAt = useCallback((index) => {
    setPricingFiles((prev) => prev.filter((_, j) => j !== index));
  }, []);

  const downloadPricingFile = useCallback((file) => {
    downloadBrowserFile(file);
  }, []);

  const onWebsiteInput = useCallback((e) => {
    e.target.value = normalizeWebsiteUrl(e.target.value);
  }, []);

  const onContactPersonInput = useCallback((e) => {
    e.target.value = formatContactPersonName(e.target.value);
  }, []);

  const onFoundingYearInput = useCallback((e) => {
    e.target.value = formatFoundingYear(e.target.value);
  }, []);

  const onSpecializationInput = useCallback((e) => {
    e.target.value = capitalizeFirstLetter(e.target.value);
  }, []);

  const onContactPositionInput = useCallback((e) => {
    e.target.value = capitalizeEachWord(e.target.value);
  }, []);

  const onReturnDetailsInput = useCallback((e) => {
    e.target.value = capitalizeFirstLetter(e.target.value);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const dropped = e.dataTransfer?.files;
      if (dropped && dropped.length) mergeFiles(Array.from(dropped));
    },
    [mergeFiles],
  );

  const onFileInputChange = useCallback(
    (e) => {
      const f = e.target.files;
      if (f && f.length) mergeFiles(Array.from(f));
      e.target.value = '';
    },
    [mergeFiles],
  );

  const showProductForm = useCallback((productId = null) => {
    if (productId != null) {
      setEditingProductId(productId);
      const p = addedProducts.find((x) => x.id === productId);
      if (p) {
        setProductDraft({
          product_group: p.group || '',
          product_name: p.name || '',
          product_brand: p.brand || '',
          product_vat: String(p.vat_percent ?? '20'),
          product_markup_manual: p.markup_percent_manual != null ? String(p.markup_percent_manual) : '',
          product_turnover: p.turnover_days != null ? String(p.turnover_days) : '',
          product_avg_sales: p.avg_sales_per_month != null ? String(p.avg_sales_per_month) : '',
        });
        setProductOpsOpen(Boolean(p.turnover_days || p.avg_sales_per_month));
      }
    } else {
      setEditingProductId(null);
      setProductDraft(emptyProductDraft());
      setProductOpsOpen(false);
    }
    setProductModalClosing(false);
    setProductFormOpen(true);
    setProductModalVisible(true);
  }, [addedProducts]);

  const finishHideProductForm = useCallback(() => {
    setProductModalVisible(false);
    setProductModalClosing(false);
    setProductFormOpen(false);
    setEditingProductId(null);
    setProductDraft(emptyProductDraft());
    setProductOpsOpen(false);
  }, []);

  const hideProductForm = useCallback(() => {
    if (!productModalVisible || productModalClosing) return;
    setProductModalClosing(true);
  }, [productModalClosing, productModalVisible]);

  const onProductModalAnimEnd = useCallback(
    (e) => {
      if (e.target !== e.currentTarget) return;
      if (productModalClosing) finishHideProductForm();
    },
    [finishHideProductForm, productModalClosing],
  );

  const addProduct = useCallback(() => {
    const group = productDraft.product_group.trim();
    const name = productDraft.product_name.trim();
    const brand = productDraft.product_brand.trim();
    const markup = parseFloat(productDraft.product_markup_manual) || 0;
    if (!group || !name || !brand || !productDraft.product_markup_manual) {
      showToast('Будь ласка, заповніть товарну групу, назву категорії, бренд та націнку.', 'validation', {
        title: 'Отакої, ви щось забули заповнити!',
      });
      return;
    }
    const product = {
      id: editingProductId != null ? editingProductId : Date.now(),
      group,
      name,
      brand,
      vat_percent: productDraft.product_vat,
      markup_percent_manual: markup,
      turnover_days: productDraft.product_turnover ? parseInt(productDraft.product_turnover, 10) : null,
      avg_sales_per_month: productDraft.product_avg_sales ? parseInt(productDraft.product_avg_sales, 10) : null,
    };
    const wasEdit = editingProductId != null;
    setAddedProducts((prev) => {
      if (wasEdit) {
        const idx = prev.findIndex((p) => p.id === editingProductId);
        const copy = prev.slice();
        copy[idx] = product;
        return copy;
      }
      return [...prev, product];
    });
    setFieldErrors((prev) => {
      if (!prev.products) return prev;
      const next = { ...prev };
      delete next.products;
      return next;
    });
    hideProductForm();
    showToast(wasEdit ? 'Товарну категорію успішно оновлено.' : 'Товарну категорію успішно додано.', 'success');
  }, [editingProductId, hideProductForm, productDraft, showToast]);

  const deleteProduct = useCallback(
    (productId) => {
      setAddedProducts((prev) => prev.filter((p) => p.id !== productId));
      showToast('Товарну категорію успішно видалено.', 'success');
    },
    [showToast],
  );

  const updateRatingUI = useCallback((score, strengths, weaknesses) => {
    const finalScore = Math.round(Number(score) || 0);
    const clamped = Math.min(Math.max(finalScore, 0), 100);
    let text = 'Потребує покращення';
    if (clamped > 85) text = 'Відмінні умови співпраці';
    else if (clamped > 70) text = 'Хороші умови співпраці';
    else if (clamped > 50) text = 'Посередні умови співпраці';
    const mainStars = Math.min(5, Math.max(0, Math.round(clamped / 20)));
    const stars = [0, 1, 2, 3, 4].map((i) => i < mainStars);
    const sList =
      strengths && strengths.length
        ? strengths.map((s) => String(s))
        : ['Критичних недоліків не виявлено'];
    const wList =
      weaknesses && weaknesses.length
        ? weaknesses.map((w) => String(w))
        : ['Явних зон для покращення не виявлено'];

    if (ratingAnimRef.current) {
      window.clearInterval(ratingAnimRef.current);
      ratingAnimRef.current = null;
    }

    setRating({
      score: 0,
      target: clamped,
      text,
      strengths: sList,
      weaknesses: wList,
      stars,
      ready: true,
    });

    if (clamped === 0) {
      setRating((prev) => ({ ...prev, score: 0 }));
      return;
    }

    let cur = 0;
    ratingAnimRef.current = window.setInterval(() => {
      cur += 1;
      setRating((prev) => ({ ...prev, score: Math.min(cur, clamped) }));
      if (cur >= clamped) {
        window.clearInterval(ratingAnimRef.current);
        ratingAnimRef.current = null;
      }
    }, 12);
  }, []);

  const buildPayload = useCallback(() => {
    if (!fields.payment_terms) {
      showToast('Будь ласка, оберіть умови оплати.', 'validation', {
        title: 'Отакої, ви щось забули заповнити!',
      });
      return null;
    }
    const electronicDocsValue = radios.electronic_docs;
    const companyData = {
      company_name: fields.company_name,
      contact_person: fields.contact_person,
      contact_position: fields.contact_position,
      contact_phone: fields.contact_phone,
      contact_email: fields.contact_email,
      website: fields.website,
      legal_address: fields.legal_address,
      bank_details: fields.bank_details,
      founding_year: fields.founding_year ? parseInt(fields.founding_year, 10) : null,
      specialization: fields.specialization,
      activity_types: Array.isArray(fields.activity_types) ? fields.activity_types : [],
      additional_info: fields.additional_info || '',
      electronic_docs: electronicDocsValue === 'yes' ? true : electronicDocsValue === 'no' ? false : null,
    };
    const certificatesValue = radios.certificates;
    const returnsValue = radios.returns;
    const deliveryValue = radios.delivery;
    const commercialData = {
      moq_units: fields.moq_units ? parseInt(fields.moq_units, 10) : null,
      moq_amount: fields.moq_amount ? parseFloat(fields.moq_amount) : null,
      payment_terms: fields.payment_terms,
      deferment_days: fields.payment_terms === 'deferment' ? parseInt(fields.deferment_days, 10) || null : null,
      returns_available: returnsValue === 'yes' ? true : returnsValue === 'no' ? false : null,
      return_details: fields.return_details,
      delivery_included: deliveryValue === 'included' ? true : deliveryValue === 'not_included' ? false : null,
      certificates_available: certificatesValue === 'yes' ? true : certificatesValue === 'no' ? false : null,
      certificates_not_required: certificatesValue === 'not_required' ? true : certificatesValue ? false : null,
    };
    const productsData = addedProducts.map((p) => ({
      group: p.group,
      name: p.name,
      brand: p.brand,
      vat_percent: p.vat_percent,
      markup_percent_manual: p.markup_percent_manual,
      turnover_days: p.turnover_days,
      avg_sales_per_month: p.avg_sales_per_month,
    }));
    return { companyData, commercialData, productsData };
  }, [addedProducts, fields, radios]);

  const buildSubmitFormData = useCallback(() => {
    const payload = buildPayload();
    if (!payload) return null;
    const { companyData, commercialData, productsData } = payload;
    const formData = new FormData();
    formData.append('company_data', JSON.stringify(companyData));
    formData.append('commercial_data', JSON.stringify(commercialData));
    formData.append('products_data', JSON.stringify(productsData));
    formData.append('fax_number', fields.fax_number || '');
    for (let i = 0; i < pricingFiles.length; i += 1) {
      const file = pricingFiles[i];
      if (file.size > MAX_FILE_BYTES) {
        showToast(`Файл "${file.name}" перевищує максимальний розмір 16MB.`, 'error');
        return null;
      }
      formData.append('pricing_files', file);
    }
    return formData;
  }, [buildPayload, fields.fax_number, pricingFiles]);

  const prepareRatingPreview = useCallback(async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setRatingLoading(true);
    setApplicationId(null);
    try {
      const payload = buildPayload();
      if (!payload) return;
      // Preview only — do not persist until «Надіслати»
      const calcResponse = await apiFetch('/api/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_data: payload.companyData,
          commercial_data: payload.commercialData,
          products_data: payload.productsData,
        }),
      });
      if (!calcResponse.ok) {
        throw new Error(
          await readApiError(calcResponse, 'Не вдалося розрахувати рейтинг. Спробуйте ще раз.'),
        );
      }
      const calcResult = await calcResponse.json();
      const insights = calcResult.insights_public || calcResult.insights;
      updateRatingUI(calcResult.score, insights?.strengths, insights?.weaknesses);
    } catch (error) {
      console.error('Error preparing rating preview:', error);
      const msg = error?.message || 'Помилка при розрахунку рейтингу. Спробуйте ще раз.';
      showToast(msg, 'error');
      updateRatingUI(0, [], [msg]);
    } finally {
      submitInFlightRef.current = false;
      setRatingLoading(false);
    }
  }, [buildPayload, updateRatingUI]);

  const submitAndSendApplication = useCallback(async () => {
    if (isSubmitting || submitFinished) return;
    setIsSubmitting(true);
    try {
      const formData = buildSubmitFormData();
      if (!formData) return;
      // One shot: DB + scores + enqueue todo.ta-da.ua (Admin odoo-worker)
      const submitResponse = await postMultipart('/api/submit', formData);
      if (!submitResponse.ok) {
        throw new Error(
          await readApiError(submitResponse, 'Помилка при відправці заявки. Спробуйте ще раз.'),
        );
      }
      const submitResult = await submitResponse.json();
      if (!submitResult.queued_for_todo) {
        throw new Error('Заявку не поставлено в чергу todo. Спробуйте ще раз.');
      }
      setApplicationId(submitResult.application_id || null);
      setSubmitFinished(true);
      setSuccessModalOpen(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      const msg = error?.message || 'Помилка при відправці заявки. Спробуйте ще раз.';
      showToast(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [buildSubmitFormData, isSubmitting, submitFinished]);

  const prepareRef = useRef(prepareRatingPreview);
  prepareRef.current = prepareRatingPreview;

  useEffect(() => {
    if (currentStep !== 4) {
      setSubmitFinished(false);
      setApplicationId(null);
      setSuccessModalOpen(false);
      return undefined;
    }
    setRating({
      score: 0,
      target: 0,
      text: '',
      strengths: [],
      weaknesses: [],
      stars: [false, false, false, false, false],
      ready: false,
    });
    const id = (step4RunRef.current += 1);
    let cancelled = false;
    (async () => {
      await prepareRef.current();
      if (cancelled || id !== step4RunRef.current) return;
    })();
    return () => {
      cancelled = true;
      if (ratingAnimRef.current) {
        window.clearInterval(ratingAnimRef.current);
        ratingAnimRef.current = null;
      }
    };
  }, [currentStep]);

  const handleFinish = useCallback(async () => {
    if (isSubmitting || ratingLoading) return;
    await submitAndSendApplication();
  }, [isSubmitting, ratingLoading, submitAndSendApplication]);

  const nextBtnLabel = useMemo(() => {
    if (currentStep === 1) return 'Продовжити';
    if (currentStep === 2) return 'Продовжити';
    if (currentStep === 3) return 'Продовжити';
    if (isSubmitting) return 'Надсилаємо…';
    if (submitFinished) return 'Надіслано';
    return 'Надіслати';
  }, [currentStep, isSubmitting, submitFinished]);


  useEffect(() => {
    if (!productModalVisible && !successModalOpen) return undefined;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyPadding = document.body.style.paddingRight;
    const scrollbarGap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.paddingRight = prevBodyPadding;
    };
  }, [productModalVisible, successModalOpen]);

  const progressWidth = currentStep === 4 ? 100 : totalProgress;

  return (
    <div className="form-page font-sans">
      <div className={`form-container${iconsReady ? ' is-animated' : ''}`}>
          <header className="form-header">
            <div className="form-header-brand">
              <img src={photo_logo} alt="TA-DA — Твій простір щастя" className="form-logo" />
            </div>
            <div className="form-header-content">
              <h1 className="form-header-title">Анкета постачальника</h1>
              <p className="form-header-subtitle">Заповніть анкету, щоб отримати доступ до платформи</p>
            </div>
          </header>

          <nav className="form-stepper" aria-label="Кроки анкети">
            {(() => {
              const steps = [
                { step: 1, label: 'Про компанію', icon: Building2 },
                { step: 2, label: 'Умови', icon: FileText },
                { step: 3, label: 'Товари', icon: Package },
                { step: 4, label: 'Результати', icon: Trophy },
              ];
              const current = steps.find((s) => s.step === currentStep) || steps[0];
              const CurrentIcon = current.icon;
              return (
                <>
                  <div className="form-stepper-mobile">
                    <div className="form-stepper-mobile-head">
                      <span className="form-stepper-mobile-badge" aria-hidden>
                        <CurrentIcon className="form-stepper-mobile-badge-icon" strokeWidth={2.25} />
                      </span>
                      <div className="form-stepper-mobile-copy">
                        <p className="form-stepper-mobile-title">{current.label}</p>
                        <p className="form-stepper-mobile-meta">Крок {currentStep} з {steps.length}</p>
                      </div>
                    </div>
                    <div className="form-stepper-mobile-track" role="presentation">
                      {steps.map(({ step, label }) => {
                        const active = currentStep === step;
                        const done = currentStep > step;
                        const disabled = !canAccessStep(step, false);
                        return (
                          <button
                            key={step}
                            type="button"
                            className={[
                              'form-stepper-mobile-seg',
                              active ? 'is-active' : '',
                              done ? 'is-done' : '',
                              disabled ? 'is-disabled' : '',
                            ].filter(Boolean).join(' ')}
                            title={disabled ? 'Заповніть всі попередні вкладки' : label}
                            aria-label={`${label} (крок ${step})`}
                            aria-current={active ? 'step' : undefined}
                            onClick={() => goToStep(step)}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-stepper-desktop">
                    {steps.map(({ step, label, icon: StepIcon }, idx, arr) => {
                      const active = currentStep === step;
                      const done = currentStep > step;
                      const pending = currentStep < step;
                      const disabled = !canAccessStep(step, false);
                      return (
                        <Fragment key={step}>
                          <button
                            type="button"
                            data-step={step}
                            className={[
                              'form-stepper-btn',
                              active ? 'is-active' : '',
                              done ? 'is-done' : '',
                              pending ? 'is-pending' : '',
                              disabled ? 'is-disabled' : '',
                            ].filter(Boolean).join(' ')}
                            title={disabled ? 'Заповніть всі попередні вкладки' : label}
                            onClick={() => goToStep(step)}
                            aria-current={active ? 'step' : undefined}
                          >
                            <span className="form-stepper-num" aria-hidden>
                              {done ? (
                                <Check className="form-stepper-check" strokeWidth={3} />
                              ) : active ? (
                                <StepIcon className="form-stepper-icon" strokeWidth={2.25} />
                              ) : null}
                            </span>
                            <span className="form-stepper-label">{label}</span>
                          </button>
                          {idx < arr.length - 1 ? (
                            <span
                              className={`form-stepper-line${currentStep > step ? ' is-done' : ''}`}
                              aria-hidden
                            />
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </nav>

          {/* progress bar hidden for now
          <div className="form-progress">
            <div className="form-progress-track" role="progressbar" aria-valuenow={progressWidth} aria-valuemin={0} aria-valuemax={100}>
              <div className="form-progress-fill" style={{ width: `${progressWidth}%` }} />
            </div>
            <span className="form-progress-pct">{progressWidth}%</span>
          </div>
          */}

          <div className="form-content" ref={formContentRef}>
            {/* STEP 1 */}
            <div id="step-1" className="step" style={{ display: currentStep === 1 ? 'block' : 'none' }}>
              <div className="form-sections">
                <FormSection
                  icon="solar:buildings-bold"
                  title="Про вашу компанію"
                  values={fields}
                  radios={radios}
                  onChange={setField}
                  onRadioChange={setRadio}
                  errors={fieldErrors}
                  fields={[
                    {
                      name: 'company_name',
                      label: 'Повна назва компанії',
                      type: 'text',
                      required: true,
                      width: 'half',
                      stackOnMobile: true,
                      placeholder: "ТОВ 'Найкращі Товари'",
                    },
                    {
                      name: 'website',
                      label: 'Адреса сайту',
                      type: 'text',
                      width: 'half',
                      stackOnMobile: true,
                      required: false,
                      placeholder: 'https://company.com',
                      onInput: onWebsiteInput,
                    },
                    {
                      name: 'legal_address',
                      label: 'Юридична адреса',
                      type: 'text',
                      required: true,
                      placeholder: '01001, м. Київ, вул. Хрещатик, 1',
                    },
                    {
                      name: 'founding_year',
                      label: 'Рік заснування',
                      type: 'number',
                      required: true,
                      width: 'half',
                      placeholder: '2010',
                      min: 1950,
                      max: CURRENT_YEAR,
                      onInput: onFoundingYearInput,
                    },
                    {
                      name: 'bank_details',
                      label: 'Банківські реквізити',
                      type: 'text',
                      required: true,
                      width: 'half',
                      placeholder: 'ЄДРПОУ, реквізити...',
                    },
                    {
                      name: 'specialization',
                      label: 'Основна спеціалізація',
                      type: 'text',
                      required: true,
                      placeholder: 'Наприклад: Автотовари, Продукти харчування',
                      onInput: onSpecializationInput,
                    },
                    {
                      name: 'activity_types',
                      label: 'Вид діяльності',
                      type: 'checkboxes',
                      required: true,
                      options: ACTIVITY_TYPE_OPTIONS,
                    },
                    {
                      name: 'additional_info',
                      label: 'Додаткова інформація',
                      type: 'textarea',
                      required: false,
                      placeholder: 'Будь-яка додаткова інформація про компанію чи умови співпраці',
                    },
                  ]}
                />

                <FormSection
                  icon="heroicons:user-solid"
                  title="Контактна особа"
                  values={fields}
                  radios={radios}
                  onChange={setField}
                  onRadioChange={setRadio}
                  errors={fieldErrors}
                  fields={[
                    {
                      name: 'contact_person',
                      label: 'Контактна особа (ПІБ)',
                      type: 'text',
                      required: true,
                      width: 'half',
                      stackOnMobile: true,
                      placeholder: 'Іваненко Іван Іванович',
                      onInput: onContactPersonInput,
                    },
                    {
                      name: 'contact_position',
                      label: 'Посада',
                      type: 'text',
                      required: true,
                      width: 'half',
                      stackOnMobile: true,
                      placeholder: 'Менеджер з продажів',
                      onInput: onContactPositionInput,
                    },
                    {
                      name: 'contact_phone',
                      label: 'Контактний телефон',
                      type: 'tel',
                      required: true,
                      width: 'half',
                      stackOnMobile: true,
                      placeholder: '+380 (50) 123-45-67',
                      inputRef: phoneMask.ref,
                      onInput: phoneMask.onInput,
                    },
                    {
                      name: 'contact_email',
                      label: 'Email',
                      type: 'email',
                      required: true,
                      width: 'half',
                      stackOnMobile: true,
                      placeholder: 'ivan.ivanenko@company.com',
                      onBlur: (e) => validateEmailInput(e.target, emailErrorRef.current),
                      footer: (
                        <span ref={emailErrorRef} id="email-error" className="field-error hidden">
                          Невірний формат email
                        </span>
                      ),
                    },
                    {
                      name: 'fax_number',
                      label: 'Факс',
                      type: 'text',
                      hidden: true,
                      tabIndex: -1,
                      autoComplete: 'off',
                    },
                  ]}
                />

                <FormSection
                  icon="basil:document-solid"
                  title="Документообіг"
                  values={fields}
                  radios={radios}
                  onChange={setField}
                  onRadioChange={setRadio}
                  errors={fieldErrors}
                  fields={[
                    {
                      name: 'electronic_docs',
                      label: 'Електронний документообіг',
                      type: 'radio',
                      required: true,
                      options: [
                        { value: 'yes', label: 'Так' },
                        { value: 'no', label: 'Ні' },
                      ],
                    },
                  ]}
                />
              </div>
            </div>

            {/* STEP 2 */}
            <div id="step-2" className="step" style={{ display: currentStep === 2 ? 'block' : 'none' }}>
              <div className="form-sections">
                <FormSection
                  icon="fluent:box-32-filled"
                  title="Мінімальні обсяги"
                  values={fields}
                  radios={radios}
                  onChange={setField}
                  onRadioChange={setRadio}
                  errors={fieldErrors}
                  fields={[
                    {
                      name: 'moq_units',
                      label: 'Мінімальний обсяг замовлення (MOQ), од.',
                      type: 'number',
                      required: false,
                      width: 'half',
                      placeholder: '100',
                      min: 0,
                    },
                    {
                      name: 'moq_amount',
                      label: 'Мінімальний обсяг замовлення (MOQ), грн',
                      type: 'number',
                      required: false,
                      width: 'half',
                      placeholder: '1000',
                      step: '0.01',
                      min: 0,
                    },
                  ]}
                />

                <FormSection
                  icon="fluent:payment-16-filled"
                  title="Оплата"
                  values={fields}
                  radios={radios}
                  onChange={setField}
                  onRadioChange={setRadio}
                  errors={fieldErrors}
                  fields={[
                    {
                      name: 'payment_terms',
                      label: 'Умови оплати',
                      type: 'select',
                      required: true,
                      options: [
                        { value: '', label: 'Оберіть умови оплати' },
                        { value: 'realization', label: 'Реалізація' },
                        { value: 'deferment', label: 'Відстрочка платежу' },
                        { value: 'prepayment', label: 'Передоплата' },
                        { value: 'postpayment', label: 'Оплата по факту' },
                      ],
                    },
                    {
                      name: 'deferment_days',
                      label: 'Термін відстрочки платежу (днів)',
                      type: 'number',
                      required: true,
                      placeholder: '90',
                      min: 1,
                      showWhen: ({ values: v }) => v.payment_terms === 'deferment',
                    },
                  ]}
                />

                <FormSection
                  icon="mage:delivery-truck-fill"
                  title="Доставка та повернення"
                  values={fields}
                  radios={radios}
                  onChange={setField}
                  onRadioChange={setRadio}
                  errors={fieldErrors}
                  fields={[
                    {
                      name: 'returns',
                      label: 'Умови повернення товару',
                      type: 'radio',
                      required: true,
                      options: [
                        { value: 'yes', label: 'Так, є повернення' },
                        { value: 'no', label: 'Ні, без повернення' },
                      ],
                    },
                    {
                      name: 'return_details',
                      label: 'Опишіть умови повернення',
                      type: 'textarea',
                      required: true,
                      placeholder: "Наприклад: 'Протягом 14 днів, за умови цілісності упаковки...'",
                      showWhen: ({ radios: r }) => r.returns === 'yes',
                      onInput: onReturnDetailsInput,
                    },
                    {
                      name: 'delivery',
                      label: 'Вартість доставки',
                      type: 'radio',
                      required: true,
                      options: [
                        { value: 'included', label: 'Включена в ціну' },
                        { value: 'not_included', label: 'Не включена' },
                      ],
                    },
                  ]}
                />

                <FormSection
                  icon="mingcute:certificate-fill"
                  title="Сертифікати"
                  values={fields}
                  radios={radios}
                  onChange={setField}
                  onRadioChange={setRadio}
                  errors={fieldErrors}
                  fields={[
                    {
                      name: 'certificates',
                      label: 'Наявність сертифікатів якості',
                      type: 'radio',
                      required: true,
                      options: [
                        { value: 'yes', label: 'Так, є сертифікати' },
                        { value: 'no', label: 'Ні, немає' },
                        { value: 'not_required', label: 'Не підлягає сертифікації' },
                      ],
                    },
                  ]}
                />
              </div>
            </div>

            <div id="step-3" className="step" style={{ display: currentStep === 3 ? 'block' : 'none' }}>
              <div className="form-sections">
                <FormSection
                  icon="mingcute:upload-3-fill"
                  title="Додайте прайс для перевірки"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    className={`form-upload${dragOver ? ' is-dragover' : ''}${pricingFiles.length > 0 ? ' has-files' : ''}${fieldErrors.pricing_files ? ' is-error' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOver(false);
                    }}
                    onDrop={onDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="pricing_file"
                      className="form-upload-input"
                      accept=".pdf,.xlsx,.xls"
                      multiple
                      onChange={onFileInputChange}
                    />

                    <div className="form-upload-body">
                      <span className="form-upload-icon" aria-hidden>
                        {pricingFiles.length > 0 ? (
                          <CheckCircle className="form-upload-icon-svg" />
                        ) : (
                          <UploadCloud className="form-upload-icon-svg" />
                        )}
                      </span>

                      {pricingFiles.length > 0 ? (
                        <>
                          <p className="form-upload-title form-upload-title--ok">
                            Вибрано файлів: {pricingFiles.length}
                          </p>
                          <p className="form-upload-hint">Натисніть, щоб додати ще</p>
                        </>
                      ) : (
                        <>
                          <p className="form-upload-title">Перетягніть файли сюди</p>
                          <p className="form-upload-hint">або натисніть для вибору</p>
                          <ul className="form-upload-types" aria-label="Дозволені формати">
                            <li>PDF</li>
                            <li>Excel</li>
                          </ul>
                          <p className="form-upload-limit">Максимум 16MB на файл</p>
                        </>
                      )}
                    </div>
                  </div>

                  {pricingFiles.length > 0 ? (
                    <ul className="form-upload-list" id="file-list">
                      {pricingFiles.map((file, i) => {
                        const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
                        const isOversize = file.size > MAX_FILE_BYTES;
                        return (
                          <li key={`${file.name}-${i}`} className={`form-upload-item${isOversize ? ' is-oversize' : ''}`}>
                            <button
                              type="button"
                              className="form-upload-item-open"
                              title="Завантажити файл"
                              onClick={() => downloadPricingFile(file)}
                            >
                              <div className="form-upload-item-icon" aria-hidden>
                                <Icon icon="solar:document-bold" height={24} width={24}></Icon>
                              </div>
                              <div className="form-upload-item-meta">
                                <span className="form-upload-item-name">{escapeHtml(file.name)}</span>
                                <span className="form-upload-item-size">
                                  {sizeInMB} MB{isOversize ? ' — перевищує ліміт!' : ''}
                                </span>
                              </div>
                            </button>
                            <button
                              type="button"
                              className="form-upload-item-remove"
                              title="Видалити"
                              aria-label={`Видалити ${file.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFileAt(i);
                              }}
                            >
                              <X className="form-upload-item-remove-icon" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </FormSection>

                <FormSection
                  icon="bxs:category-alt"
                  title="Категорії для розрахунку рейтингу"
                >
                  <div className="form-products">
                    {addedProducts.length === 0 ? (
                      <button
                        type="button"
                        id="empty-product-list"
                        className={`form-products-cta${fieldErrors.products ? ' is-error' : ''}`}
                        onClick={() => showProductForm(null)}
                      >
                        <span className="form-products-cta-icon" aria-hidden>
                          <PlusCircle className="form-products-cta-icon-svg" />
                        </span>
                        <span className="form-products-cta-copy">
                          <span className="form-products-cta-title">Додати першу категорію</span>
                          <span className="form-products-cta-hint">
                            Потрібна хоча б одна — без неї рейтинг не порахується
                          </span>
                        </span>
                        <span className="form-products-cta-go">Додати</span>
                      </button>
                    ) : null}

                    {addedProducts.length > 0 ? (
                      <ul className="form-products-list" id="product-list">
                        {addedProducts.map((product) => {
                          const vat =
                            product.vat_percent === 'no' || product.vat_percent === 'Без ПДВ'
                              ? 'Без ПДВ'
                              : product.vat_percent != null && product.vat_percent !== ''
                                ? `${product.vat_percent}%`
                                : '—';
                          return (
                            <li
                              key={product.id}
                              className="form-product-card"
                              role="button"
                              tabIndex={0}
                              onClick={() => showProductForm(product.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  showProductForm(product.id);
                                }
                              }}
                            >
                              <div className="form-product-card-top">
                                <h3 className="form-product-card-title">{escapeHtml(product.name)}</h3>
                                <div className="form-product-card-actions">
                                  <button
                                    type="button"
                                    className="form-product-action"
                                    title="Редагувати"
                                    aria-label="Редагувати"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      showProductForm(product.id);
                                    }}
                                  >
                                    <Edit2 className="form-product-action-icon" />
                                  </button>
                                  <button
                                    type="button"
                                    className="form-product-action form-product-action--danger"
                                    title="Видалити"
                                    aria-label="Видалити"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteProduct(product.id);
                                    }}
                                  >
                                    <Trash2 className="form-product-action-icon" />
                                  </button>
                                </div>
                              </div>
                              <dl className="form-product-card-grid">
                                <div className="form-product-stat form-product-stat--group">
                                  <Layers className="form-product-stat-icon" aria-hidden />
                                  <div className="form-product-stat-text">
                                    <dt>Група</dt>
                                    <dd>{escapeHtml(product.group)}</dd>
                                  </div>
                                </div>
                                {product.brand ? (
                                  <div className="form-product-stat form-product-stat--brand">
                                    <Tag className="form-product-stat-icon" aria-hidden />
                                    <div className="form-product-stat-text">
                                      <dt>Бренд</dt>
                                      <dd>{escapeHtml(product.brand)}</dd>
                                    </div>
                                  </div>
                                ) : null}
                                <div className="form-product-stat form-product-stat--vat">
                                  <Percent className="form-product-stat-icon" aria-hidden />
                                  <div className="form-product-stat-text">
                                    <dt>ПДВ</dt>
                                    <dd>{escapeHtml(vat)}</dd>
                                  </div>
                                </div>
                                <div className="form-product-stat form-product-stat--markup">
                                  <TrendingUp className="form-product-stat-icon" aria-hidden />
                                  <div className="form-product-stat-text">
                                    <dt>Націнка</dt>
                                    <dd>{escapeHtml(String(product.markup_percent_manual || 0))}%</dd>
                                  </div>
                                </div>
                                {product.turnover_days ? (
                                  <div className="form-product-stat form-product-stat--turnover">
                                    <Clock className="form-product-stat-icon" aria-hidden />
                                    <div className="form-product-stat-text">
                                      <dt>Оборотність</dt>
                                      <dd>{escapeHtml(String(product.turnover_days))} днів</dd>
                                    </div>
                                  </div>
                                ) : null}
                                {product.avg_sales_per_month ? (
                                  <div className="form-product-stat form-product-stat--sales">
                                    <ShoppingBag className="form-product-stat-icon" aria-hidden />
                                    <div className="form-product-stat-text">
                                      <dt>Продажі</dt>
                                      <dd>{escapeHtml(String(product.avg_sales_per_month))} од./міс.</dd>
                                    </div>
                                  </div>
                                ) : null}
                              </dl>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}

                    {addedProducts.length > 0 ? (
                      <button
                        id="add-product-btn"
                        type="button"
                        className="form-products-cta"
                        onClick={() => showProductForm(null)}
                      >
                        <span className="form-products-cta-icon" aria-hidden>
                          <PlusCircle className="form-products-cta-icon-svg" />
                        </span>
                        <span className="form-products-cta-copy">
                          <span className="form-products-cta-title">Додати ще категорію</span>
                          <span className="form-products-cta-hint">
                            Можна додати кілька — рейтинг врахує всі
                          </span>
                        </span>
                        <span className="form-products-cta-go">Додати</span>
                      </button>
                    ) : null}
                  </div>
                </FormSection>
              </div>
            </div>

            <div id="step-4" className="step" style={{ display: currentStep === 4 ? 'block' : 'none' }}>
              <div className="form-results">
                <header className="form-results-hero">
                  <span className="form-results-hero-icon" aria-hidden>
                    <Trophy className="form-results-hero-svg" />
                  </span>
                  <h2 className="form-results-hero-title">
                    {ratingLoading ? 'Розраховуємо рейтинг…' : 'Ваш рейтинг розраховано'}
                  </h2>
                  <p className="form-results-hero-sub">
                    {ratingLoading
                      ? 'Зберігаємо дані та рахуємо оцінку. Зачекайте хвилинку.'
                      : 'Детальна оцінка якості та умов співпраці'}
                  </p>
                </header>

                <div className={`form-results-score${ratingLoading ? ' is-loading' : ''}`}>
                  <div className="form-results-ring-wrap">
                    {ratingLoading ? (
                      <div className="form-results-loading-inner">
                        <Loader2 className="form-results-spinner" aria-hidden />
                        <span>Завантаження…</span>
                      </div>
                    ) : (
                      <>
                        <svg className="form-results-ring" viewBox="0 0 36 36" aria-hidden>
                          <circle
                            className="form-results-ring-track"
                            cx="18"
                            cy="18"
                            r="15.9155"
                            fill="none"
                            pathLength="100"
                          />
                          <circle
                            className="form-results-ring-fill"
                            cx="18"
                            cy="18"
                            r="15.9155"
                            fill="none"
                            pathLength="100"
                            strokeDasharray={
                              rating.score > 0
                                ? `${rating.score} ${100 - rating.score}`
                                : '0 100'
                            }
                            strokeLinecap={rating.score > 0 ? 'round' : 'butt'}
                            transform="rotate(-90 18 18)"
                          />
                        </svg>
                        <div className="form-results-score-num">
                          <span className="form-results-score-value">{rating.score}</span>
                          <span className="form-results-score-max">/100</span>
                        </div>
                      </>
                    )}
                  </div>

                  {!ratingLoading && rating.ready ? (
                    <div className="form-results-score-meta">
                      <p className="form-results-score-label">Загальний бал</p>
                      <p className="form-results-score-text">{rating.text}</p>
                      <div className="form-results-stars" aria-label={`${rating.stars.filter(Boolean).length} з 5 зірок`}>
                        {rating.stars.map((on, i) => (
                          <Star
                            key={i}
                            className={`form-results-star${on ? ' is-on' : ''}`}
                            aria-hidden
                          />
                        ))}
                      </div>
                      <p className="form-results-tip">
                        Ви можете повернутися на попередні кроки, змінити відповіді та покращити рейтинг.
                        Коли будете готові — натисніть «Надіслати».
                      </p>
                    </div>
                  ) : null}
                </div>

                {!ratingLoading && rating.ready ? (
                  <div className="form-results-insights">
                    <section className="form-results-card form-results-card--ok">
                      <h3 className="form-results-card-title">
                        <span className="form-results-card-icon form-results-card-icon--ok" aria-hidden>
                          <ThumbsUp className="form-results-card-svg" />
                        </span>
                        Сильні сторони
                      </h3>
                      <ul className="form-results-list">
                        {rating.strengths.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </section>
                    <section className="form-results-card form-results-card--warn">
                      <h3 className="form-results-card-title">
                        <span className="form-results-card-icon form-results-card-icon--warn" aria-hidden>
                          <Zap className="form-results-card-svg" />
                        </span>
                        Зони для покращення
                      </h3>
                      <ul className="form-results-list">
                        {rating.weaknesses.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </section>
                  </div>
                ) : null}

              </div>
            </div>
          </div>

          <div className="form-footer">
            {currentStep > 1 ? (
              <button
                type="button"
                id="prev-btn"
                onClick={prevStep}
                disabled={ratingLoading || isSubmitting}
                className="form-footer-btn form-footer-btn--back"
              >
                <ArrowLeft className="form-footer-btn-icon" />
                Повернутися
              </button>
            ) : (
              <span className="form-footer-spacer" aria-hidden />
            )}
            <button
              type="button"
              id="next-btn"
              onClick={currentStep === TOTAL_STEPS ? handleFinish : nextStep}
              disabled={
                ratingLoading
                || isSubmitting
                || (currentStep === TOTAL_STEPS && (!rating.ready || submitFinished))
              }
              className="form-footer-btn form-footer-btn--next"
              aria-busy={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="form-footer-btn-icon form-footer-btn-icon--spin" /> : null}
              {nextBtnLabel}
            </button>
          </div>
      </div>

      {productModalVisible
        ? createPortal(
            <div
              className={`form-product-modal${productModalClosing ? ' is-closing' : ' is-open'}`}
              role="presentation"
              onClick={(e) => {
                if (e.target === e.currentTarget) hideProductForm();
              }}
              onAnimationEnd={onProductModalAnimEnd}
            >
              <div
                id="product-form"
                className="form-product-form form-product-modal-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="product-form-title"
              >
                <div className="form-product-form-top">
                  <h3 id="product-form-title" className="form-product-form-title">
                    <Icon icon="iconamoon:category-fill" className="form-product-form-title-icon"></Icon>
                    {editingProductId != null
                      ? 'Редагування категорії'
                      : 'Нова товарна категорія'}
                  </h3>
                  <button
                    type="button"
                    className="form-product-form-close"
                    onClick={hideProductForm}
                    aria-label="Закрити"
                  >
                    <X className="form-product-form-close-icon" />
                  </button>
                </div>

                <div className="form-product-form-fields">
                  <div className="form-group">
                    <label className="form-label" htmlFor="product_group">
                      <span>
                        Товарна група
                        <span className="form-required" aria-hidden> *</span>
                      </span>
                    </label>
                    <CustomSelect
                      id="product_group"
                      required
                      placeholder="Оберіть групу"
                      value={productDraft.product_group}
                      aria-label="Товарна група"
                      onChange={(next) => setProductDraft((p) => ({ ...p, product_group: next }))}
                      options={productGroups.map((g) => ({ value: g.name, label: g.name }))}
                    />
                  </div>

                  <div className="form-product-form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="product_name">
                        <span>
                          Назва категорії
                          <span className="form-required" aria-hidden> *</span>
                        </span>
                      </label>
                      <input
                        id="product_name"
                        className="form-input"
                        value={productDraft.product_name}
                        onChange={(e) =>
                          setProductDraft((p) => ({
                            ...p,
                            product_name: capitalizeFirstLetter(e.target.value),
                          }))
                        }
                        placeholder="Назва категорії"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="product_brand">
                        <span>
                          Бренд
                          <span className="form-required" aria-hidden> *</span>
                        </span>
                      </label>
                      <input
                        id="product_brand"
                        className="form-input"
                        value={productDraft.product_brand}
                        onChange={(e) =>
                          setProductDraft((p) => ({
                            ...p,
                            product_brand: capitalizeFirstLetter(e.target.value),
                          }))
                        }
                        placeholder="Назва бренду"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-product-form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="product_vat">
                        <span>
                          ПДВ
                          <span className="form-required" aria-hidden> *</span>
                        </span>
                      </label>
                      <CustomSelect
                        id="product_vat"
                        value={productDraft.product_vat}
                        aria-label="ПДВ"
                        onChange={(next) => setProductDraft((p) => ({ ...p, product_vat: next }))}
                        options={[
                          { value: '20', label: '20%' },
                          { value: '7', label: '7%' },
                          { value: '14', label: '14%' },
                          { value: '0', label: '0%' },
                          { value: 'no', label: 'Без ПДВ' },
                        ]}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="product_markup_manual">
                        <span>
                          Націнка (%)
                          <span className="form-required" aria-hidden> *</span>
                        </span>
                      </label>
                      <input
                        type="number"
                        id="product_markup_manual"
                        className="form-input"
                        value={productDraft.product_markup_manual}
                        onChange={(e) =>
                          setProductDraft((p) => ({ ...p, product_markup_manual: e.target.value }))
                        }
                        placeholder="45"
                        step="0.1"
                        min={0}
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`form-product-ops-toggle${productOpsOpen ? ' is-open' : ''}`}
                    aria-expanded={productOpsOpen}
                    onClick={() => setProductOpsOpen((v) => !v)}
                  >
                    <Activity className="form-product-ops-toggle-icon" />
                    <span>Додаткові параметри</span>
                    <span className="form-product-ops-toggle-hint">не обовʼязково</span>
                    <ChevronDown className="form-product-ops-chevron" />
                  </button>

                  {productOpsOpen ? (
                    <div className="form-product-form-row form-product-ops">
                      <div className="form-group">
                        <label className="form-label" htmlFor="product_turnover">
                          Оборотність (днів)
                        </label>
                        <input
                          type="number"
                          id="product_turnover"
                          className="form-input"
                          value={productDraft.product_turnover}
                          onChange={(e) =>
                            setProductDraft((p) => ({ ...p, product_turnover: e.target.value }))
                          }
                          placeholder="30"
                          min={1}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="product_avg_sales">
                          Продажі (од./міс. на ТТ)
                        </label>
                        <input
                          type="number"
                          id="product_avg_sales"
                          className="form-input"
                          value={productDraft.product_avg_sales}
                          onChange={(e) =>
                            setProductDraft((p) => ({ ...p, product_avg_sales: e.target.value }))
                          }
                          placeholder="10"
                          min={1}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="form-product-form-footer">
                  <button
                    type="button"
                    onClick={hideProductForm}
                    className="form-product-form-btn form-product-form-btn--ghost"
                  >
                    Скасувати
                  </button>
                  <button
                    type="button"
                    onClick={addProduct}
                    className="form-product-form-btn form-product-form-btn--primary"
                  >
                    <CheckCircle className="form-product-form-btn-icon" />
                    Зберегти
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {successModalOpen
        ? createPortal(
            <div
              className="form-success-modal"
              role="presentation"
              onClick={(e) => {
                if (e.target === e.currentTarget) resetFormToStart();
              }}
            >
              <div
                className="form-success-modal-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="form-success-title"
              >
                <span className="form-success-modal-icon" aria-hidden>
                  <CheckCircle className="form-success-modal-icon-svg" />
                </span>
                <h2 id="form-success-title" className="form-success-modal-title">
                  Заявку надіслано!
                </h2>
                <p className="form-success-modal-text">
                  Заявку успішно надіслано. Ми зв&apos;яжемося з вами найближчим часом.
                </p>
                <button
                  type="button"
                  className="form-success-modal-btn"
                  onClick={resetFormToStart}
                 >
                  Добре
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
